<template lang="pug">
.d-flex.align-center.gap-16.ga-4
  dv-text-field(
    v-if="search"
    v-model="searchKeys[search.filter]"
    :disabled="loading"
    v-bind="{ ...DEFAULT_SEARCH_ATTRS, ...search.attrs }"
    @keyup.enter="runSearch"
    @input="debouncedSearch"
    v-on="search.listeners"
  )
  slot(name="search-field--filters" v-bind="{ filters, query, loading, method, searchKeys, runSearch }")
    template(v-if="filters.length")
      component(
        v-for="filterData in filters"
        :key="filterData.filter"
        :is="filterComponent(filterData)"
        :value="filterValue(filterData)"
        :disabled="loading || filterData?.attrs?.disabled"
        v-bind="filterAttrs(filterData)"
        @input="(value) => handleFilterInput(value, filterData, runSearch)"
        v-on="filterData.listeners || filterData?.component?.listeners"
      )

</template>

<script>
import DvMinifySelect from '@/packages/ui/components/inputs/DvMinifySelect.vue';
import DvTextField from '@/packages/ui/components/inputs/DvTextField.vue';
import { debounce } from 'lodash';

const DEFAULT_SEARCH_ATTRS = {
  outlined: false,
  height: 44,
  solo: true,
  flat: true,
  'min-width': 400,
  'background-color': 'ui-background',
  clearable: true,
  hideDetails: true,
};

const DEBOUNCE_DELAY = 900;

export default {
  components: {
    DvTextField,
    DvMinifySelect,
  },
  props: {
    /** @SearchItem { filter: String, attrs: Object, listeners: Object } */
    search: { type: Object, default: null }, // { ...SearchItem, debounced: boolean }
    filters: { type: Array, default: () => ([]) }, // SearchItem[]
    query: { type: Object, default: () => ({}) },
    method: { type: Function, default: () => ({}) },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      DEFAULT_SEARCH_ATTRS,
      searchKeys: { ...this.query },

      debouncedSearch: () => {},
    };
  },
  computed: {},
  watch: {
    searchKeys: {
      handler(val) {
        if (this.search && (!val[this.search.filter])) {
          this.runSearch();
        }
        this.$emit('update:query', { ...val });
      },
      deep: true,
    },
    query: {
      handler(val) {
        if (JSON.stringify(val) !== JSON.stringify(this.searchKeys)) {
          this.searchKeys = { ...val };
        }
      },
    },
  },
  created() {
    if (this.search?.debounced) {
      this.debouncedSearch = debounce(this.searchText, DEBOUNCE_DELAY);
    }
  },
  methods: {
    filterComponent(filterData) {
      return filterData?.component?.is || DvMinifySelect;
    },
    filterAttrs(filterData) {
      return filterData?.component?.attrs || filterData?.attrs || {};
    },
    filterValue(filterData) {
      if (filterData?.component?.mode === 'query') {
        return this.searchKeys;
      }
      return this.searchKeys[filterData.filter];
    },
    handleFilterInput(value, filterData, runSearch) {
      if (filterData?.component?.mode === 'query') {
        const { searchList = [] } = filterData;
        searchList.forEach((key) => {
          if (Object.prototype.hasOwnProperty.call(this.searchKeys, key)) {
            this.$delete(this.searchKeys, key);
          }
        });

        Object.entries(value || {}).forEach(([key, itemValue]) => {
          if (itemValue === undefined || itemValue === null || itemValue === '') {
            if (Object.prototype.hasOwnProperty.call(this.searchKeys, key)) {
              this.$delete(this.searchKeys, key);
            }
            return;
          }

          this.$set(this.searchKeys, key, itemValue);
        });
      } else if (value === undefined || value === null || value === '') {
        this.$delete(this.searchKeys, filterData.filter);
      } else {
        this.$set(this.searchKeys, filterData.filter, value);
      }

      runSearch();
    },
    searchText(text) {
      if (this.query[this.search?.filter] !== text) {
        this.runSearch();
      }
    },
    runSearch() {
      if (this.loading) return;
      this.method(this.searchKeys);
    },
  },
};
</script>

<style lang="scss" scoped></style>
